<script lang="ts">
import type { RouteLocationRaw } from '#vue-router'

export type BeforeItem = {
  title: stringI18n
  to: RouteLocationRaw | undefined
}

export type BreadcrumItem = {
  key: string
  title: string
  department: string | None
  to: RouteLocationRaw | undefined
  toLast?: RouteLocationRaw | undefined
  sup?: string | number
  last?: boolean
  lastnoline?: boolean
  start: boolean
}

export type HiddenBreadcrum = { name: string; to: RouteLocationRaw }

export function useBreadcums(
  propsInp: () => {
    before?: BeforeItem[] | None
    lastSup?: string | number
    nolast?: boolean
    catalog?: gen.SearchCatalogSellerBreadcrumsFragment[] | None
  }
): globalThis.ComputedRef<BreadcrumItem[]> {
  const { myLink } = useLinks()
  return computed((): BreadcrumItem[] => {
    const props = propsInp()
    const arr = [
      ...helpers.arr(props.before, (item, index): Omit<BreadcrumItem, 'start'> => ({
        key: `1:${index}`,
        title: item.title,
        department: undefined,
        to: item?.to,
      })),
      ...helpers.arr(props.catalog, (item, index): Omit<BreadcrumItem, 'start'> | None => {
        const slug = item?.slug
        if (!slug) return
        return {
          key: `2:${index}`,
          department: slug,
          title: helpers.onlyString(item?.title),
          to: myLink('search-all', { all: item.type === 'Department' ? ['d', slug] : [slug] }),
        }
      }),
    ]

    const indexLast = arr.length - 1
    const { lastSup, nolast } = props

    return arr.map((item, index): BreadcrumItem => {
      const last = nolast ? false : index === indexLast
      return {
        key: item.key,
        title: item.title,
        to: last ? undefined : item.to,
        toLast: item.to,
        department: last ? undefined : item.department,
        sup: last ? lastSup : undefined,
        last,
        start: !index,
      }
    })
  })
}
</script>

<script setup lang="ts">
const props = defineProps<{
  pagename: string | None
  list: BreadcrumItem[]
  hidden?: HiddenBreadcrum[]
  nowrap?: boolean
}>()

const init3 = useInited('init3')
const contElem = useTemplateRef('cont')
const elem = shallowRef<HTMLElement>()
const activeKey = shallowRef<string>()
const open = shallowRef(false)
const render = shallowRef(false)
const timer2 = useTimeout()
watch(
  () => props.list,
  () => {
    activeKey.value = undefined
    timer2.set(() => contElem.value?.scroll({ left: 999, behavior: 'smooth' }), 100)
  }
)

const timer = useTimeout()
function mouseenter(department: string | None, e: unknown | MouseEvent) {
  if (!department) timer.clear()
  else {
    timer.set(
      () => {
        elem.value = e instanceof MouseEvent ? helpers.getHTMLElement(e.target) : undefined
        if (department && !render.value) render.value = true
        activeKey.value = department || undefined
      },
      activeKey.value ? 0 : 100
    )
  }
}
</script>

<template>
  <div ref="cont" class="_ff3 _fs08 _lh17 _row _fw-l _ctrY _hm22 _ls-xs _pr1" :class="nowrap ? '_w100p _scrollX' : '_wrap'" aria-label="Breadcrumb">
    <LazyBreadcrumMenu v-if="render" v-model:department="activeKey" v-model:open="open" :elem="() => elem" :list />
    <template v-for="(item, index) in list" :key="index">
      <span v-if="item.start && index" class="_ml1 _mr1 _c1">/</span>
      <LinkFalling
        v-if="item.to"
        :txt="item.title"
        :to="item.to"
        :class="[$style.txt, item.last && 'last', open && activeKey && activeKey === item.department && 'on']"
        @mouseenter.passive="(e: unknown) => mouseenter(item.department, e)"
        @mouseleave.passive="(e: unknown) => mouseenter(undefined, e)"
      />
      <span v-else :class="[$style.txt, item.last && 'last', open && activeKey && activeKey === item.department && 'on']">{{ item.title }}</span>
      <sup v-if="helpers.isDef(item.sup)" :class="$style.sup">{{ helpers.zeroPad(item.sup) }}</sup>
      <span v-if="!item.last && !item.lastnoline" class="_svg12 _ctr">
        <LazyIconTriangleRight v-if="init3" :class="$style.i" />
      </span>
    </template>
  </div>
</template>

<style module>
.sup {
  composes: global(_br14);
  composes: global(_pr04);
  composes: global(_pl04);
  composes: global(_fw-l);
  composes: global(_c0);

  background: var(--blue3);
  line-height: 11px;
}
.i {
  composes: global(_relative);
  top: -1px;
  color: hsl(240 6% 80%);
}

.txt {
  composes: global(_noflex);
  composes: global(_ws_nowrap);
}
.txt:not(:global(.last)):hover {
  color: var(--blue3);
}
.txt:global(.on) {
  color: var(--blue2);
}
.txt:global(.last) {
  color: var(--black);
  text-decoration: 1px underline;
  text-underline-offset: 4px;
}
.txt:global(.last) .innter {
  text-decoration: 1px underline;
  text-underline-offset: 2px;
}
</style>
